import React, { useState } from 'react';
import { 
  Volume2, TrendingUp, Sparkles, ArrowRight, 
  Brain, FileText, Zap, Globe, Layers, Award, Users, 
  Activity, Star, ChevronRight, Check, HeartHandshake,
  MessageSquare, Eye, Compass, GraduationCap, Stethoscope,
  ClipboardList, CreditCard, MessageCircle, Box, Bot,
  Calculator, ChevronDown, ChevronUp, ShieldCheck, Lock, Play, RotateCcw
} from 'lucide-react';

interface SaaSLandingPageProps {
  onOpenAuth: () => void;
  onExploreDemo: (tab: any) => void;
  onSelectModule?: (moduleId: string) => void;
}

export const SaaSLandingPage: React.FC<SaaSLandingPageProps> = ({ onOpenAuth, onExploreDemo, onSelectModule }) => {
  const [pricingCycle, setPricingCycle] = useState<'monthly' | 'annual'>('annual');
  
  // Interactive ROI Calculator State
  const [studentCount, setStudentCount] = useState(75);
  const [therapistCount, setTherapistCount] = useState(12);

  // FAQ Accordion State
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  // Live Quick Sandbox State
  const [sandboxPhrase, setSandboxPhrase] = useState('I want water');
  const [sandboxVoiceRate, setSandboxVoiceRate] = useState(1);

  const handleSpeakSandbox = (text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = sandboxVoiceRate;
      window.speechSynthesis.speak(u);
    }
  };

  const calculatedHoursSaved = Math.round(studentCount * 1.8 + therapistCount * 6);
  const calculatedCostSavings = Math.round(calculatedHoursSaved * 35 * 12);

  const modulesList = [
    {
      id: 'aac',
      name: 'AAC Speech Synthesizer Module',
      category: 'Assistive Communication',
      icon: Volume2,
      color: 'bg-blue-100 text-blue-700',
      description: 'Augmentative speech board with real-time text-to-speech synthesis, symbol grids, and multi-lingual support.',
      features: [
        'Web Speech API Text-to-Speech Engine with Pitch & Speed Controls',
        'Multi-Lingual Voice Support (English, Hindi, Marathi)',
        'Dynamic High-Contrast Symbol Communication Grid',
        'Real-Time Sentence Builder & One-Click Voice Playback',
        'Custom Phrase Bank & Emergency Fast-Response Buttons'
      ]
    },
    {
      id: 'speech-therapy',
      name: 'Speech Therapy & Articulation Module',
      category: 'Clinical Therapy',
      icon: Volume2,
      color: 'bg-teal-100 text-teal-700',
      description: 'Phonetic analysis, vocal exercises, and sound articulation progress tracking for speech pathologists.',
      features: [
        'Acoustic Pitch & Vocal Frequency Spectrogram Visualizer',
        'Phonetic Articulation Drills (/r/, /s/, /th/, /l/, /k/)',
        'Real-Time Microphone Audio Recording & Waveform Analyzer',
        'Automated Pronunciation Accuracy Scoring (0-100%)',
        'Vocal Fatigue & Respiration Exercise Timers'
      ]
    },
    {
      id: 'autism',
      name: 'Autism & ABA Behavior Module',
      category: 'Behavioral Health',
      icon: Brain,
      color: 'bg-purple-100 text-purple-700',
      description: 'Applied Behavior Analysis (ABA) session logging, sensory triggers, and positive reinforcement trackers.',
      features: [
        'Antecedent-Behavior-Consequence (ABC) Incident Logger',
        'Real-Time Behavior Frequency & Duration Counters',
        'Target Skill Acquisition Progress Tracker',
        'Sensory Trigger Identification & Environmental Log',
        'Reinforcer Preference Inventory & Token Reward System'
      ]
    },
    {
      id: 'sign-language',
      name: 'Sign Language & Gesture Module',
      category: 'Accessible Learning',
      icon: Eye,
      color: 'bg-indigo-100 text-indigo-700',
      description: 'Indian Sign Language (ISL) & ASL video recognition, flashcards, and gesture-to-text translation.',
      features: [
        'Indian Sign Language (ISL) & ASL Video Gesture Dictionary',
        'Real-Time Camera Gesture Recognition Engine Simulation',
        'Interactive Fingerspelling Practice & Alphabet Cards',
        'Gesture-to-Text & Text-to-Sign Translation',
        'Multi-Speed Gesture Demonstration Video Player'
      ]
    },
    {
      id: 'braille',
      name: 'Braille & Tactile Learning Module',
      category: 'Visual Impairment',
      icon: Compass,
      color: 'bg-amber-100 text-amber-700',
      description: 'Grade 1 & 2 Braille translation, screen reader integration, and tactile learning exercises.',
      features: [
        'English to Grade 1 & Grade 2 Nemeth Braille Converter',
        'Real-Time 6-Dot Braille Cell Pattern Visualizer',
        'Screen Reader Audio Hints & High-Contrast Focus Rings',
        'Refreshable Braille Display Terminal Emulator',
        'Tactile Map Audio Marker & Spatial Learning Prompts'
      ]
    },
    {
      id: 'student',
      name: 'Student & IEP Progress Module',
      category: 'Special Education',
      icon: GraduationCap,
      color: 'bg-emerald-100 text-emerald-700',
      description: 'Individualized Education Program (IEP) goals, milestone tracking, and student performance metrics.',
      features: [
        'SMART IEP Goal Builder with Domain Categorization',
        'Interactive Progress Percentage Sliders (-5% / +5%)',
        'Accommodations & Modifications Matrix (Extended Time, Visual Prompts)',
        'Annual Review Countdown & Compliance Timeline',
        'Student Strength Profile & Transition Milestone Tracker'
      ]
    },
    {
      id: 'therapist',
      name: 'Therapist Workspace & SOAP Notes Module',
      category: 'Clinical Operations',
      icon: Stethoscope,
      color: 'bg-sky-100 text-sky-700',
      description: 'Standardized SOAP progress notes, therapy scheduling, and clinical case history logs.',
      features: [
        'Standardized Subjective, Objective, Assessment, Plan (SOAP) Editor',
        'One-Click Clinical Signature & Time-Stamp Lock',
        'CPT Medical Billing Code Auto-Complete Tagging',
        'Daily Patient Attendance Roster & Quick Status Check',
        'Integrated Student Health Record Sync'
      ]
    },
    {
      id: 'teacher',
      name: 'Teacher Classroom & Lesson Module',
      category: 'School Education',
      icon: Users,
      color: 'bg-rose-100 text-rose-700',
      description: 'Specialized lesson plans, inclusive classroom activities, and student engagement logs.',
      features: [
        'Inclusive Differentiated Lesson Plan Builder',
        'Classroom Accommodation & Assistive Tech Checklist',
        'Student Daily Behavior & Classroom Participation Roster',
        'Sensory-Friendly Room Layout & Transition Prompts',
        'Group Activity & Peer Support Pair Matrix'
      ]
    },
    {
      id: 'assessment',
      name: 'Diagnostic Clinical Assessment Module',
      category: 'Evaluations',
      icon: ClipboardList,
      color: 'bg-violet-100 text-violet-700',
      description: 'Standardized developmental benchmarks, sensory profiles, and diagnostic intake evaluations.',
      features: [
        'Sensory Profile 2 Standardized Rating Scale Calculator',
        'CARS-2 (Childhood Autism Rating Scale) Evaluator',
        'Vineland-3 Adaptive Behavior Composite Scoring',
        'Automated Diagnostic Impression Summary Generator',
        'Multi-Disciplinary Intake Assessment History'
      ]
    },
    {
      id: 'school-erp',
      name: 'Special School ERP & Timetable Module',
      category: 'Administration',
      icon: Layers,
      color: 'bg-blue-100 text-blue-800',
      description: 'Class scheduling, attendance tracking, specialized transport routes, and staff management.',
      features: [
        'Wheelchair Accessible Special Transport Bus GPS Route Tracker',
        'Adaptive Classroom Timetable & Room Allocation Engine',
        'Teacher & Therapist Specialization Duty Roster',
        'Student Daily Attendance & Absence Reason Tracker',
        'Parent Notification & School Calendar Sync'
      ]
    },
    {
      id: 'hospital',
      name: 'Rehabilitation Hospital Module',
      category: 'Inpatient Care',
      icon: Activity,
      color: 'bg-red-100 text-red-700',
      description: 'Inpatient therapy rounds, discharge planning, multi-disciplinary care team coordination.',
      features: [
        'Inpatient Ward & Pediatric Rehab Bed Occupancy Manager',
        'Multidisciplinary Team Care Rounds Logging (Physiatrist, OT, SLP)',
        'Patient Vital Sign Trend Tracker & Alert Dashboard',
        'Discharge Readiness Scorecard (0-100%)',
        'Post-Operative Rehabilitation Protocol Builder'
      ]
    },
    {
      id: 'billing',
      name: 'NDIS & Clinical Insurance Billing Module',
      category: 'Financial Management',
      icon: CreditCard,
      color: 'bg-green-100 text-green-700',
      description: 'Automated claim generation, NDIS support item catalog, and therapy session invoicing.',
      features: [
        'NDIS Support Item Code Catalog Search (CB Daily Activity, Capacity Building)',
        'Automated Invoice Generator with Tax & Rate Rules',
        'Real-Time Claim Status Pipeline (Paid, Pending, Rejected)',
        'Pre-Authorization & Funding Balance Tracker',
        'One-Click Export to Xero / QuickBooks / Excel'
      ]
    },
    {
      id: 'communication',
      name: 'Parent-Teacher Communication Module',
      category: 'Family Engagement',
      icon: MessageCircle,
      color: 'bg-orange-100 text-orange-700',
      description: 'Secure messaging, home practice video sharing, and daily student activity logs.',
      features: [
        'Secure Direct Messaging & Team Group Chats',
        'Daily Home Practice Video & Photo Gallery Stream',
        'Student Daily Mood & Emotion Check-In Tracker',
        'Appointment Reminder Notifications',
        'Multi-Lingual Auto-Translation for Parent Messages'
      ]
    },
    {
      id: 'inventory',
      name: 'Assistive Device Inventory Module',
      category: 'Resource Management',
      icon: Box,
      color: 'bg-slate-200 text-slate-800',
      description: 'Wheelchair, hearing aid, and AAC hardware lending, maintenance, and serial allocation.',
      features: [
        'Wheelchair, Hearing Aid & AAC Hardware Lending Register',
        'Equipment Serial Number Barcode / QR Code Scanner',
        'Maintenance Calibration & Battery Replacement Alerts',
        'Digital Asset Loan Agreement & Return Date Tracker',
        'Equipment Sanitization & Inspection History'
      ]
    },
    {
      id: 'analytics',
      name: 'Executive Analytics & BI Dashboard Module',
      category: 'Reporting',
      icon: TrendingUp,
      color: 'bg-cyan-100 text-cyan-700',
      description: 'Cross-departmental progress insights, outcome reports, and institutional performance metrics.',
      features: [
        'Institutional IEP Compliance Percentage Gauges',
        'Therapist Capacity & Clinical Utilization Charts',
        'Multi-School Outcome & Mastered Goal Metrics',
        'Executive PDF & Excel Audit Report Exporter',
        'Historical Trend Analysis (Quarterly / Annual)'
      ]
    },
    {
      id: 'ai',
      name: 'AI Copilot & Report Summarizer Module',
      category: 'Intelligence',
      icon: Bot,
      color: 'bg-pink-100 text-pink-700',
      description: 'Automated IEP goal generation, progress note summarizing, and clinical recommendations.',
      features: [
        'Automated SMART IEP Goal Generation Prompt Engine',
        'Clinical SOAP Note & Therapy Trial Summarizer',
        'Parent Progress Letter & Plain-Language Report Writer',
        'Clinical Risk Detection & Early Intervention Alerts',
        'Multi-Lingual AI Output Generator (English, Hindi, Marathi)'
      ]
    }
  ];

  const faqs = [
    {
      q: 'How does AbilityOS ensure security and compliance (HIPAA / FERPA / DISHA)?',
      a: 'AbilityOS employs end-to-end AES-256 encryption at rest and TLS 1.3 in transit. Every student health record and IEP plan is strictly isolated per tenant organization, compliant with HIPAA, FERPA, GDPR, and DISHA medical data privacy standards.'
    },
    {
      q: 'Can AbilityOS be deployed directly on our existing cPanel / WHM server?',
      a: 'Yes! AbilityOS is engineered specifically for native WHM/cPanel environments using MultiPHP 8.4 and MariaDB. It requires no complex server configurations and can be deployed with one-click using our cPanel Git deployment script.'
    },
    {
      q: 'How do School Administrators bulk upload student records?',
      a: 'Admins can download our standard CSV/Excel template, populate student details (Roll ID, Name, Grade, Disability Category, Guardian Phone), and upload the file in seconds. The system automatically validates data format and populates student rosters.'
    },
    {
      q: 'Is the AAC Speech Synthesizer compatible with eye-gaze and switch access devices?',
      a: 'Yes, all 16 modules adhere to WCAG 2.2 AAA accessibility guidelines, supporting eye-gaze cameras (Tobii Dynavox), Bluetooth switch buttons, screen readers, and high-contrast accessibility modes.'
    },
    {
      q: 'Does AbilityOS support multi-lingual voices and regional sign languages?',
      a: 'AbilityOS natively supports English, Hindi, and Marathi text-to-speech synthesis and includes dictionary datasets for both Indian Sign Language (ISL) and American Sign Language (ASL).'
    }
  ];

  return (
    <div className="bg-slate-50 text-slate-900 min-h-screen">
      
      {/* HERO SECTION */}
      <section className="relative pt-12 pb-20 px-6 overflow-hidden bg-gradient-to-b from-blue-950 via-blue-900 to-slate-900 text-white">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-blue-500/10 blur-3xl rounded-full pointer-events-none" />
        
        <div className="max-w-7xl mx-auto text-center relative z-10 space-y-8">
          
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-200 text-xs font-semibold backdrop-blur-md">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span>Complete Operating System for Special Education & Rehabilitation</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-tight max-w-5xl mx-auto">
            Empowering Every Student & Therapist with <span className="bg-gradient-to-r from-blue-300 via-sky-200 to-indigo-300 bg-clip-text text-transparent">AI-Driven Precision</span>
          </h1>

          <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto font-normal leading-relaxed">
            AbilityOS brings together AAC speech synthesis, automated IEP goal management, speech therapy, autism ABA tracking, and 16 specialized modules into one unified enterprise platform.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <button
              onClick={onOpenAuth}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold rounded-xl shadow-xl shadow-blue-500/25 transition transform hover:-translate-y-0.5 flex items-center justify-center gap-3 text-base"
            >
              <span>Get Started Free</span>
              <ArrowRight className="w-5 h-5" />
            </button>

            <button
              onClick={() => onExploreDemo('aac')}
              className="w-full sm:w-auto px-8 py-4 bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold rounded-xl backdrop-blur-md transition flex items-center justify-center gap-2 text-base"
            >
              <Zap className="w-5 h-5 text-amber-400" />
              <span>Try Live AAC Speech Synthesizer</span>
            </button>
          </div>

          {/* Key Metrics Strip */}
          <div className="pt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto text-left">
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <div className="text-3xl font-extrabold text-blue-300">50,000+</div>
              <div className="text-xs text-blue-200 mt-1 font-medium">Students & Beneficiaries Supported</div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <div className="text-3xl font-extrabold text-blue-300">1,400+</div>
              <div className="text-xs text-blue-200 mt-1 font-medium">Special Schools & Rehabilitation Centers</div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <div className="text-3xl font-extrabold text-blue-300">16 Modules</div>
              <div className="text-xs text-blue-200 mt-1 font-medium">Specialized Education & Care Suite</div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <div className="text-3xl font-extrabold text-blue-300">WCAG 2.2 AAA</div>
              <div className="text-xs text-blue-200 mt-1 font-medium">Accessible for Assistive Devices</div>
            </div>
          </div>

        </div>
      </section>

      {/* LIVE QUICK SANDBOX DEMO WIDGET */}
      <section className="py-12 px-6 max-w-5xl mx-auto -mt-10 relative z-20">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-2xl space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <span className="text-xs font-bold text-blue-600 flex items-center gap-1.5 uppercase tracking-wider">
              <Zap className="w-4 h-4 text-amber-500 fill-current" />
              Live Interactive Sandbox (No Login Needed)
            </span>
            <span className="text-xs text-slate-400 font-medium">Try AAC Speech Synthesis Below</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <input
              type="text"
              value={sandboxPhrase}
              onChange={(e) => setSandboxPhrase(e.target.value)}
              placeholder="Type phrase to speak..."
              className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none w-full"
            />
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => handleSpeakSandbox(sandboxPhrase)}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs flex items-center gap-2 shadow transition"
              >
                <Play className="w-4 h-4 fill-current" />
                Speak Voice
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ALL 16 MODULES GRID SECTION */}
      <section className="py-16 px-6 max-w-7xl mx-auto">
        <div className="text-center space-y-4 mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-full uppercase tracking-wider">
            Complete Platform Capabilities
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900">
            Explore All 16 AbilityOS Enterprise Modules
          </h2>
          <p className="text-slate-600 max-w-3xl mx-auto text-base">
            Every module is built with strict accessibility standards, multi-lingual support, and specialized workflows tailored for schools, therapy centers, and rehabilitation hospitals.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {modulesList.map((module) => {
            const IconComponent = module.icon;
            return (
              <div 
                key={module.id}
                onClick={() => onSelectModule?.(module.id)}
                className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group cursor-pointer hover:border-blue-500 hover:-translate-y-1"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className={`w-12 h-12 rounded-xl ${module.color} flex items-center justify-center group-hover:scale-110 transition`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <span className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                      {module.category}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition">
                      {module.name}
                    </h3>
                    <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                      {module.description}
                    </p>
                  </div>

                  {/* Comprehensive Feature Bullet List */}
                  <div className="pt-3 border-t border-slate-100 space-y-2">
                    <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Included Key Capabilities:</div>
                    <ul className="space-y-1.5 text-[11px] text-slate-700 font-medium">
                      {module.features.map((feat, fidx) => (
                        <li key={fidx} className="flex items-start gap-1.5 leading-snug">
                          <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 mt-4 flex items-center justify-between text-xs font-bold text-blue-600">
                  <span>Launch Interactive Module</span>
                  <ChevronRight className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition" />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* INTERACTIVE ROI & TIME SAVINGS CALCULATOR */}
      <section className="py-16 px-6 bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto space-y-10">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-bold rounded-full uppercase tracking-wider border border-emerald-500/30">
              <Calculator className="w-4 h-4" /> Institutional Impact Calculator
            </div>
            <h2 className="text-3xl md:text-4xl font-black">Estimate Your School / Clinic Savings</h2>
            <p className="text-slate-400 text-sm max-w-2xl mx-auto">
              See how automated SOAP notes, AI SMART IEP generation, and instant NDIS billing reduce administrative overhead for your staff.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-950 p-8 rounded-2xl border border-slate-800 shadow-2xl">
            <div className="space-y-6">
              <div>
                <div className="flex justify-between text-xs font-bold mb-2">
                  <span>Enrolled Students / Patients:</span>
                  <span className="text-emerald-400 font-mono text-base">{studentCount} Students</span>
                </div>
                <input 
                  type="range" min="10" max="500" step="5"
                  value={studentCount} onChange={(e) => setStudentCount(parseInt(e.target.value))}
                  className="w-full accent-emerald-500 cursor-pointer"
                />
              </div>

              <div>
                <div className="flex justify-between text-xs font-bold mb-2">
                  <span>Therapists & Special Educators:</span>
                  <span className="text-emerald-400 font-mono text-base">{therapistCount} Staff</span>
                </div>
                <input 
                  type="range" min="2" max="50" step="1"
                  value={therapistCount} onChange={(e) => setTherapistCount(parseInt(e.target.value))}
                  className="w-full accent-emerald-500 cursor-pointer"
                />
              </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-xl border border-slate-800 flex flex-col justify-between space-y-4 text-center md:text-left">
              <div>
                <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Estimated Monthly Hours Saved</div>
                <div className="text-4xl font-black text-emerald-400 mt-1">{calculatedHoursSaved} Hours / Month</div>
              </div>

              <div>
                <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Estimated Annual Cost Savings</div>
                <div className="text-3xl font-black text-blue-400 mt-1">${calculatedCostSavings.toLocaleString()} / Year</div>
              </div>

              <div className="text-[11px] text-slate-500 border-t border-slate-800 pt-3">
                *Based on standard 1.8 hrs saved per student on IEP drafting and 6 hrs/month per therapist on clinical SOAP documentation.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ ACCORDION SECTION */}
      <section className="py-16 px-6 max-w-4xl mx-auto space-y-8">
        <div className="text-center space-y-3">
          <h2 className="text-3xl font-black text-slate-900">Frequently Asked Questions</h2>
          <p className="text-slate-600 text-xs">Everything you need to know about deployment, compliance, and platform features.</p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div 
                key={idx}
                onClick={() => setOpenFaq(isOpen ? null : idx)}
                className="bg-white border border-slate-200 rounded-xl p-4 cursor-pointer transition shadow-sm hover:shadow"
              >
                <div className="flex items-center justify-between font-bold text-sm text-slate-900">
                  <span>{faq.q}</span>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-blue-600" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
                {isOpen && (
                  <p className="text-xs text-slate-600 mt-3 pt-3 border-t border-slate-100 leading-relaxed">
                    {faq.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

    </div>
  );
};
